<script setup lang="ts">
import { ref, computed, watch, onBeforeUnmount } from 'vue';
import type { ItpDataGridColumn, GridSortEntry } from './types';
import { columnKey } from './gridCell';
import { gridMessages } from '../../messages';

const props = withDefaults(
  defineProps<{
    columns: ItpDataGridColumn[];
    gridTemplateColumns: string;
    hasSystemColumn?: boolean;
    canSelect?: boolean;
    allSelected?: boolean;
    someSelected?: boolean;
    resizable?: boolean;
    reorderable?: boolean;
    isSortable: (field: string) => boolean;
    sortState: (field: string) => {
      direction: GridSortEntry['direction'] | null;
      priority: number | null;
    };
  }>(),
  {
    hasSystemColumn: false,
    canSelect: false,
    allSelected: false,
    someSelected: false,
    resizable: true,
    reorderable: true,
  },
);

const emit = defineEmits<{
  'toggle-all': [];
  'sort-column': [column: ItpDataGridColumn];
  'resize-column': [field: string, width: number];
  'freeze-columns': [entries: { key: string; width: number }[]];
  'move-column': [fromField: string, toField: string];
  'autofit-column': [field: string];
}>();

const headerColumns = computed(() =>
  props.columns.map((column) => ({
    column,
    sortable: props.isSortable(columnKey(column)),
    sort: props.sortState(columnKey(column)),
  })),
);

const checkbox = ref<HTMLInputElement | null>(null);
watch(
  () => props.someSelected,
  (partial) => {
    if (checkbox.value) {
      checkbox.value.indeterminate = partial;
    }
  },
  { flush: 'post' },
);

const resizing = ref<{
  field: string;
  startX: number;
  startWidth: number;
} | null>(null);

let resizeMoved = false;
let swallowNextClick = false;

function freezeColumnWidths(cell: HTMLElement): void {
  if (!props.columns.some((column) => !column.width)) {
    return;
  }
  const header = cell.closest('.itp-data-grid__header');
  if (!header) {
    return;
  }
  const entries: { key: string; width: number }[] = [];
  header
    .querySelectorAll<HTMLElement>('.itp-data-grid__hcell[data-column]')
    .forEach((element) => {
      const key = element.dataset.column;
      if (key) {
        entries.push({
          key,
          width: Math.round(element.getBoundingClientRect().width),
        });
      }
    });
  if (entries.length) {
    emit('freeze-columns', entries);
  }
}

function onResizeStart(field: string, event: MouseEvent): void {
  const cell = (event.target as HTMLElement).closest(
    '.itp-data-grid__hcell',
  ) as HTMLElement | null;
  if (!cell) {
    return;
  }
  freezeColumnWidths(cell);
  resizeMoved = false;
  resizing.value = {
    field,
    startX: event.clientX,
    startWidth: cell.getBoundingClientRect().width,
  };
  window.addEventListener('mousemove', onResizeMove);
  window.addEventListener('mouseup', onResizeEnd);
}

function onResizeMove(event: MouseEvent): void {
  const current = resizing.value;
  if (!current) {
    return;
  }
  if (event.clientX !== current.startX) {
    resizeMoved = true;
  }
  emit(
    'resize-column',
    current.field,
    current.startWidth + (event.clientX - current.startX),
  );
}

function onResizeEnd(): void {
  if (resizeMoved) {
    swallowNextClick = true;
    setTimeout(() => {
      swallowNextClick = false;
    }, 0);
  }
  resizeMoved = false;
  resizing.value = null;
  window.removeEventListener('mousemove', onResizeMove);
  window.removeEventListener('mouseup', onResizeEnd);
}

onBeforeUnmount(onResizeEnd);

const draggingField = ref<string | null>(null);
const dropTargetField = ref<string | null>(null);

function onDragStart(field: string, event: DragEvent): void {
  if (!props.reorderable || resizing.value) {
    event.preventDefault();
    return;
  }
  draggingField.value = field;
  event.dataTransfer!.effectAllowed = 'move';
  event.dataTransfer!.setData('text/plain', field);
}

function onDragOver(field: string, event: DragEvent): void {
  if (!draggingField.value) {
    return;
  }
  event.preventDefault();
  event.dataTransfer!.dropEffect = 'move';
  dropTargetField.value = field;
}

function onDrop(field: string): void {
  if (draggingField.value && draggingField.value !== field) {
    emit('move-column', draggingField.value, field);
  }
  onDragEnd();
}

function onDragEnd(): void {
  draggingField.value = null;
  dropTargetField.value = null;
}

function onCellClick(column: ItpDataGridColumn): void {
  if (resizing.value || swallowNextClick) {
    swallowNextClick = false;
    return;
  }
  emit('sort-column', column);
}
</script>

<template>
  <div class="itp-data-grid__header" :style="{ gridTemplateColumns }">
    <div
      v-if="hasSystemColumn"
      class="itp-data-grid__hcell itp-data-grid__syscell"
    >
      <input
        v-if="canSelect"
        ref="checkbox"
        type="checkbox"
        class="itp-data-grid__rowcheck"
        :checked="allSelected"
        :title="gridMessages.selectAll"
        @change="$emit('toggle-all')"
      />
    </div>
    <div
      v-for="{ column, sortable, sort } in headerColumns"
      :key="columnKey(column)"
      class="itp-data-grid__hcell"
      :data-column="columnKey(column)"
      :class="[
        {
          'is-sortable': sortable,
          'is-dragging': draggingField === columnKey(column),
          'is-drop-target':
            dropTargetField === columnKey(column) &&
            draggingField !== columnKey(column),
        },
      ]"
      :draggable="reorderable && !resizing"
      @click="onCellClick(column)"
      @dragstart="onDragStart(columnKey(column), $event)"
      @dragover="onDragOver(columnKey(column), $event)"
      @drop="onDrop(columnKey(column))"
      @dragend="onDragEnd"
    >
      <span
        class="itp-data-grid__hlabel"
        :title="column.toolTip || undefined"
        :data-own-title="column.toolTip ? '' : undefined"
        >{{ column.name || column.field }}</span
      >
      <span v-if="sort.direction" class="itp-data-grid__sort">
        {{ sort.direction === 'asc' ? '▲' : '▼' }}
        <span v-if="sort.priority" class="itp-data-grid__sort-rank">{{
          sort.priority
        }}</span>
      </span>
      <span
        v-if="resizable"
        class="itp-data-grid__resizer"
        :title="gridMessages.resizeColumn"
        @click.stop
        @dragstart.prevent.stop
        @mousedown.stop.prevent="onResizeStart(columnKey(column), $event)"
        @dblclick.stop.prevent="$emit('autofit-column', columnKey(column))"
      />
    </div>
  </div>
</template>
